import { ErrorMessage } from "@components/atoms/ErrorMessage"
import { Button } from "@components/controls/Button"
import { SubmitBar } from "@components/controls/SubmitBar"
import { LoadingResults } from "@components/layout/LoadingResults"
import { ErrorPanel } from "@components/layout/non-ideal-states"
import { SidebarLayout } from "@components/page/SidebarLayout"
import { IconCaretLeft } from "@components/svg/Icons"
import { CartControllerSummary } from "@features/accounts/components/CartSummary"
import { CheckoutForm } from "@features/accounts/components/CheckoutForm"
import { ChoosePlan } from "@features/accounts/components/ChoosePlan"
import { LoginForm } from "@features/accounts/components/LoginForm"
import { RegisterAccount } from "@features/accounts/components/RegisterAccount"
import { useAuth, useMembershipStatus } from "@features/accounts/hooks/useAuth"
import { useJoinProcedure } from "@features/accounts/hooks/useJoinProcess"
import { MembershipSummary } from "@features/accounts/screens/MembershipScreen"
import { useBusinessTeamInviteByHash } from "@hooks/queries"
import { Link, useIsSSR, useRoute, useRouter } from "eddev/routing"
import { defineView } from "eddev/views"
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useMemo } from "react"
import { twMerge } from "tailwind-merge"

export default defineView("membership/join", (props) => {
  const auth = useAuth()
  const route = useRoute()
  const router = useRouter()
  const state = useJoinProcedure()
  const phase = state.phases.find((phase) => phase.active || (state.complete && phase.key === "Complete"))
  const status = useMembershipStatus()

  const inviteHash = useMemo(() => {
    if (env.client) {
      const url = new URL(document.location.href)
      const hash = url.searchParams.get("team-invite")
      return hash
    }
  }, [])
  const isRedeemingInvite = !!inviteHash

  const inviteLookup = useBusinessTeamInviteByHash(
    { hash: inviteHash! },
    {
      enabled: isRedeemingInvite,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      staleTime: 0,
    }
  )
  const invite = inviteLookup.data?.teamInvitationByHash

  useEffect(() => {
    document.scrollingElement?.scrollTo({ top: 0, behavior: "smooth" })
  }, [phase?.key])

  useEffect(() => {
    if (isRedeemingInvite) return
    if (status.hasAccess && !status.expiresSoon) {
      router.navigate("/dashboard")
    }
  }, [status.hasAccess])

  useEffect(() => {
    if (invite?.plan) {
      state.startFromInvite(invite)
    }
  }, [!!invite])

  if (useIsSSR()) {
    return null
  }

  if (isRedeemingInvite && !state.complete) {
    if (isRedeemingInvite && inviteLookup.isLoading) {
      return <LoadingResults />
    } else if (isRedeemingInvite && inviteLookup.isError) {
      return <ErrorPanel title="An error occurred while processing this request" message={inviteLookup.error.message} />
    } else if (isRedeemingInvite && !invite) {
      return <ErrorPanel title="This link has expired" message="The invitation link you're using is no longer valid. Please contact the member who sent you the invite." />
    }
  }

  return (
    <SidebarLayout
      navContent={
        <ul>
          {state.phases.map((phase) => (
            <li key={phase.key} className={twMerge("type-ui-m-light pb-[0.5em]")}>
              <button
                className={twMerge(
                  !phase.canAccess && "cursor-default opacity-50",
                  "data-[active]:decoration-fg/40 decoration-fg/0 underline underline-offset-[0.1em] transition-colors text-left"
                )}
                data-active={phase.active ? "" : undefined}
                onClick={(e) => {
                  if (phase.canAccess && !state.complete) {
                    state.goTo(phase.key)
                  }
                  return e.preventDefault()
                }}
              >
                {phase.label}
              </button>
            </li>
          ))}
        </ul>
      }
    >
      {/* Different phases */}
      <AnimatePresence mode="wait">
        <motion.div key={state.currentPhase} initial={{ opacity: 0 }} exit={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {/* Choose plan */}
          {state.currentPhase === "ChoosePlan" && (
            <>
              <PageHeader title={state.isRenewal ? "Renew your membership" : "Become an AGDA Member"} subtitle="Select membership type" />
              <ChoosePlan
                data={props}
                intent="join"
                onChoose={(plan, product) => {
                  state.start(plan.slug!, product?.databaseId!)
                }}
              />
            </>
          )}

          {/* Accept Invite (if team invite) */}
          {state.currentPhase === "AcceptInvite" && (
            <>
              <PageHeader title={`Team Membership`} subtitle={`You've been invited to join a team membership`} />
              <div className="stack-y-4">
                <p className="type-body-m-roman">{invite?.businessTeam?.owner?.name} has invited you to join their design business membership.</p>
                <p className="type-body-m-roman">This invitation is for {invite?.email} only.</p>
                <ErrorMessage>
                  {state.inviteState === "swap-account" ? (
                    <p>
                      You're currently signed in with {auth.user?.email}, but this invitation is for {invite?.email}. You'll need to either change your email address in{" "}
                      <Link href="/account">Account Settings</Link>, or sign out.
                    </p>
                  ) : null}
                </ErrorMessage>
              </div>
              <div className="mt-5">
                {state.inviteState === "create-account" ? (
                  <Button onClick={() => state.acceptInvite()}>Create Account</Button>
                ) : state.inviteState === "swap-account" ? (
                  <Button
                    onClick={() => {
                      auth.logout()
                      setTimeout(() => inviteLookup.refetch(), 100)
                    }}
                  >
                    Sign Out
                  </Button>
                ) : state.inviteState === "ready" ? (
                  <Button onClick={() => state.acceptInvite()}>Accept Invite</Button>
                ) : state.inviteState === "login" ? (
                  <Button onClick={() => state.acceptInvite()}>Sign In</Button>
                ) : null}
              </div>
            </>
          )}

          {/* Sign In (if team invite) */}
          {state.currentPhase === "SignIn" && (
            <>
              {/* <PageHeader title="Team Membership" subtitle="Sign In" /> */}
              {
                <LoginForm
                  loginReason={`You must sign in to accept the invitation.`}
                  onSuccess={() => {
                    state.goTo(null)
                  }}
                  disableSignup={!!invite?.email}
                />
              }
            </>
          )}

          {/* Create Account */}
          {state.currentPhase === "CreateAccount" && (
            <>
              <PageHeader title="Become an AGDA Member" subtitle="Account Setup" />
              <RegisterAccount
                onComplete={() => {
                  state.markCreatedUser()
                  state.goTo("Confirm")
                }}
                onGoBack={() => state.goBack()}
                forceEmail={invite?.email ?? undefined}
              />
            </>
          )}

          {/* Welcome */}
          {state.currentPhase === "Confirm" && (
            <>
              {state.isAlreadyUser ? (
                <PageHeader title={`Welcome to AGDA, ${auth.user?.name}.`} subtitle="Payment summary" />
              ) : (
                <PageHeader title={`Welcome to AGDA, ${auth.user?.name}.`} subtitle="Account created" />
              )}
              <div className="type-title-xs-medium border-b border-black pb-3">Order Summary</div>
              <CartControllerSummary controller={state.cart} />
              <SubmitBar
                submitLabel="Continue"
                onClick={() => {
                  state.goTo(null)
                  state.confirmDetails()
                }}
              >
                <Button onClick={() => state.goTo("ChoosePlan")} leftIcon={<IconCaretLeft />} variant="outline">
                  Back
                </Button>
              </SubmitBar>
            </>
          )}

          {/* Billing details */}
          {state.currentPhase === "BillingDetails" && (
            <>
              {state.isAlreadyUser ? (
                <PageHeader title={`Welcome to AGDA, ${auth.user?.name}.`} subtitle="Subscription and billing" />
              ) : (
                <PageHeader title={`Welcome to AGDA, ${auth.user?.name}.`} subtitle="Subscription and billing" />
              )}
              <CheckoutForm
                controller={state.cart}
                onGoBack={() => state.goTo("Confirm")}
                agreements={{
                  membership:
                    "I support AGDA's mission of developing excellence in graphic design and do hereby agree to be bound by the AGDA Constitution and Code of Ethics which are available at www.agda.com.au or by contacting the AGDA National Office. I agree that my membership of AGDA does not allow the use of the AGDA identity, name or logo for endorsement of my business, product or services.",
                  terms: "I agree to AGDA’s Terms and Conditions and Privacy Policy. ",
                  newsletter: "I'd like to receive AGDA's newsletter and other communications.",
                }}
                onSuccess={() => {
                  state.goTo("Complete")
                  state.markComplete()
                }}
              />
            </>
          )}

          {/* Success */}
          {state.currentPhase === "Complete" && (
            <>
              <PageHeader title={state.isRenewal ? "Success! Your membership has been renewed." : "Success! You are now an AGDA member."} subtitle="Membership information" />
              <MembershipSummary data={state.membership} />
              <div className="mt-5">
                <Button href="/dashboard">Go to my account</Button>
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </SidebarLayout>
  )
})

function PageHeader(props: { title: string; subtitle?: string }) {
  return (
    <div className="mb-6">
      <h2 className="!type-title-l-medium">{props.title}</h2>
      {!!props.subtitle && <div className="!type-title-l-roman mt-1">{props.subtitle}</div>}
    </div>
  )
}
